import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import { createContext, useContext, type ReactNode } from 'react'

import { api, ApiError, type Me } from './api'

/**
 * 当前用户 + 权限 + 可见菜单，全部由服务端下发。
 *
 * 前端不自行推导权限——`/api/me` 返回的 permissions 是服务端用同一份矩阵算出来的。
 * 即便有人改了前端让按钮显示出来，真正的拦截仍在后端 require() 上，
 * 这里的作用只是别让用户看见点不了的东西。
 */

const SessionContext = createContext<UseQueryResult<Me, unknown> | null>(null)

export function useMeQuery() {
  return useQuery<Me>({
    queryKey: ['me'],
    queryFn: () => api.get<Me>('/me'),
    retry: (count, error) => {
      // 401 是「没登录」，重试没有意义
      if (error instanceof ApiError && error.status === 401) return false
      return count < 2
    },
    staleTime: 60_000,
  })
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const query = useMeQuery()
  return <SessionContext.Provider value={query}>{children}</SessionContext.Provider>
}

export function useSessionQuery() {
  const ctx = useContext(SessionContext)
  if (!ctx) throw new Error('useSessionQuery 必须在 SessionProvider 内使用')
  return ctx
}

export function useMe(): Me | undefined {
  return useSessionQuery().data
}

/**
 * 权限判断。用法：`const can = useCan(); can('token', 'hide')`
 *
 * 数据源是服务端下发的 permissions 列表，没登录或还在加载时一律返回 false（fail-closed）。
 */
export function useCan() {
  const me = useMe()
  return (resource: string, action: string): boolean =>
    me?.permissions.includes(`${resource}:${action}`) ?? false
}

/** 按权限包裹内容。无权限时渲染 fallback（默认什么都不渲染）。 */
export function Can({
  resource,
  action,
  children,
  fallback = null,
}: {
  resource: string
  action: string
  children: ReactNode
  fallback?: ReactNode
}) {
  const can = useCan()
  return <>{can(resource, action) ? children : fallback}</>
}
